import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Edit,
  Key,
  Ban,
  CheckCircle,
  Trash2,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileUpload } from '@/components/shared/FileUpload';
import { FormSection } from '@/components/shared/FormSection';
import { PageHeader } from '@/components/shared/PageHeader';
import { EditPageLayout } from '@/components/shared/EditPageLayout';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { useAuth } from '@/hooks/useAuth';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';
import {
  getUserById,
  updateUser,
  deleteUser,
  resetUserPassword,
  toggleUserActive,
  uploadUserImage,
  cancelInvitation,
  type UpdateUserPayload,
} from '@/services/userService';
import type { User, UserRole } from '@packages/shared-types';

// Schema for root users (can assign root or admin)
const rootUserFormSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().email('validation.emailInvalid').optional(),
  role: z.enum(['root', 'admin']).optional(),
});

// Schema for tenant users (can assign admin or user)
const tenantUserFormSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().email('validation.emailInvalid').optional(),
  role: z.enum(['admin', 'user']).optional(),
});

export function UserDetailsPage(): JSX.Element {
  const { t } = useTranslation();
  const { id: idParam } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user: currentUser, refreshUser } = useAuth();
  const { activeTenant } = useTenantSwitcher();
  
  // If on /profile route, use current user's ID; otherwise use id from params
  const id = location.pathname === '/profile' ? currentUser?.id : idParam;
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] =
    useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isToggleActiveDialogOpen, setIsToggleActiveDialogOpen] =
    useState(false);
  const [isCancelInvitationDialogOpen, setIsCancelInvitationDialogOpen] =
    useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTogglingActive, setIsTogglingActive] = useState(false);
  const [isCancelingInvitation, setIsCancelingInvitation] = useState(false);

  // Determine if editing a tenant user or root user
  const isTenantUser = useMemo(() => {
    return user?.tenantId !== null && user?.role !== 'root';
  }, [user]);

  const userFormSchema = isTenantUser ? tenantUserFormSchema : rootUserFormSchema;
  type UserFormValues = z.infer<typeof userFormSchema>;

  const {
    register,
    handleSubmit,
    control,
    reset: resetForm,
    formState: { errors },
  } = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: isTenantUser ? 'user' : 'admin',
    },
  });

  const getUserDisplayName = useCallback((user: User): string => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) {
      return user.firstName;
    }
    return user.email;
  }, []);

  const getInviterDisplayName = useCallback((user: User): string => {
    if (!user.inviter) {
      return '-';
    }
    const { inviter } = user;
    if (inviter.firstName && inviter.lastName) {
      return `${inviter.firstName} ${inviter.lastName}`;
    }
    if (inviter.firstName) {
      return inviter.firstName;
    }
    return inviter.email;
  }, []);

  const formatDate = useCallback((date: Date | string): string => {
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, []);

  const isPending = useCallback((user: User): boolean => {
    return Boolean(user.invitationToken);
  }, []);

  const canEditRole = useCallback((): boolean => {
    return currentUser?.role === 'root';
  }, [currentUser]);

  const isCurrentUser = useCallback((): boolean => {
    return currentUser?.id === id;
  }, [currentUser, id]);

  const loadUser = useCallback(async (): Promise<void> => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await getUserById(id, activeTenant?.id);
      setUser(data);
      setProfileImageUrl(data.profileImage ?? null);
      resetForm({
        firstName: data.firstName ?? '',
        lastName: data.lastName ?? '',
        email: data.email,
        role: data.role,
      });
      setSelectedFile(null);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('pages.users.details.error.load'),
      );
    } finally {
      setIsLoading(false);
    }
  }, [id, resetForm, t, activeTenant]);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  const handleSelectFile = useCallback((file: File | null) => {
    setSelectedFile(file);
  }, []);

  const handleSubmitUser = async (values: UserFormValues): Promise<void> => {
    if (!id || !user) return;
    setIsSaving(true);

    try {
      const payload: UpdateUserPayload = {
        firstName: values.firstName || undefined,
        lastName: values.lastName || undefined,
        email: values.email || undefined,
        role: canEditRole() ? (values.role as UserRole) : undefined,
      };

      await updateUser(id, payload, activeTenant?.id);

      if (selectedFile) {
        await uploadUserImage(id, selectedFile, activeTenant?.id);
      }

      await loadUser();
      setSelectedFile(null);
      setIsEditMode(false);
      
      // Refresh AuthContext if user updated their own profile
      if (isCurrentUser()) {
        await refreshUser();
      }
      
      toast.success(t('pages.users.details.actions.saveSuccess'));
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('pages.users.details.error.update'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void handleSubmit(handleSubmitUser)();
  };

  const handleCancelEdit = (): void => {
    setIsEditMode(false);
    void loadUser();
  };

  const handleResetPassword = async (): Promise<void> => {
    if (!id) return;
    setIsResettingPassword(true);
    try {
      await resetUserPassword(id, activeTenant?.id);
      toast.success(t('pages.users.details.actions.resetPasswordSuccess'));
      setIsResetPasswordDialogOpen(false);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('pages.users.details.error.resetPassword'),
      );
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleToggleActive = async (): Promise<void> => {
    if (!id) return;
    setIsTogglingActive(true);
    try {
      const updatedUser = await toggleUserActive(id, activeTenant?.id);
      setUser(updatedUser);
      toast.success(
        updatedUser.isActive
          ? t('pages.users.details.actions.enableSuccess')
          : t('pages.users.details.actions.disableSuccess'),
      );
      setIsToggleActiveDialogOpen(false);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('pages.users.details.error.toggleActive'),
      );
    } finally {
      setIsTogglingActive(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await deleteUser(id, activeTenant?.id);
      toast.success(t('pages.users.details.actions.deleteSuccess'));
      navigate('/users');
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('pages.users.details.error.delete'),
      );
      setIsDeleting(false);
    }
  };

  const handleCancelInvitation = async (): Promise<void> => {
    if (!id) return;
    setIsCancelingInvitation(true);
    try {
      await cancelInvitation(id, activeTenant?.id);
      toast.success(t('pages.users.details.actions.cancelInvitationSuccess'));
      setIsCancelInvitationDialogOpen(false);
      // Always navigate back to users page after cancelling invitation
      navigate('/users', { replace: true });
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('pages.users.details.error.cancelInvitation'),
      );
    } finally {
      setIsCancelingInvitation(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('pages.users.details.title')}
          description={t('pages.users.details.loading')}
        />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('pages.users.details.title')}
          description={t('pages.users.details.error.notFound')}
        />
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-destructive">
                {t('pages.users.details.error.notFound')}
              </p>
              <Button variant="outline" onClick={() => navigate(-1)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('pages.users.details.actions.back')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const displayName = getUserDisplayName(user);
  const inviterName = getInviterDisplayName(user);
  const pending = isPending(user);
  const isActive = user.isActive !== false; // Default to true if undefined

  if (isEditMode) {
    return (
      <EditPageLayout
        title={displayName}
        description={t('pages.users.details.description')}
        isLoading={false}
        formId="user-edit-form"
        onSubmit={handleFormSubmit}
        onCancel={handleCancelEdit}
        cancelLabel={t('pages.users.details.actions.cancel')}
        onSave={() => void handleSubmit(handleSubmitUser)()}
        saveLabel={t('pages.users.details.actions.save')}
        savingLabel={t('pages.users.details.actions.saving')}
        isSaving={isSaving}
        entityExists={!!user}
      >
        <div className="space-y-8">
          <FormSection
            title={t('pages.users.details.edit.imageLabel')}
            description={t('pages.users.details.edit.imageHelper')}
          >
            <FileUpload
              currentImageUrl={profileImageUrl}
              onFileChange={handleSelectFile}
              disabled={isSaving}
              showLabel={false}
            />
          </FormSection>
          <form
            id="user-edit-form"
            className="space-y-6"
            onSubmit={handleSubmit(handleSubmitUser)}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <FormSection
                title={t('pages.users.details.fields.firstName')}
                error={errors.firstName?.message}
              >
                <Input
                  id="user-firstName"
                  {...register('firstName')}
                  disabled={isSaving}
                />
              </FormSection>
              <FormSection
                title={t('pages.users.details.fields.lastName')}
                error={errors.lastName?.message}
              >
                <Input
                  id="user-lastName"
                  {...register('lastName')}
                  disabled={isSaving}
                />
              </FormSection>
            </div>
            <FormSection
              title={t('pages.users.details.fields.email')}
              error={errors.email?.message}
            >
              <Input
                id="user-email"
                type="email"
                {...register('email')}
                disabled={isSaving}
              />
            </FormSection>
            {canEditRole() && (
              <FormSection
                title={t('pages.users.details.fields.role')}
                error={errors.role?.message}
              >
                <Controller
                  control={control}
                  name="role"
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isSaving}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {isTenantUser ? (
                          <>
                            <SelectItem value="admin">
                              {t('pages.users.invites.roles.administrator')}
                            </SelectItem>
                            <SelectItem value="user">
                              {t('pages.users.invites.roles.user')}
                            </SelectItem>
                          </>
                        ) : (
                          <>
                            <SelectItem value="root">
                              {t('pages.users.invites.roles.root')}
                            </SelectItem>
                            <SelectItem value="admin">
                              {t('pages.users.invites.roles.support')}
                            </SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  )}
                />
              </FormSection>
            )}
          </form>
        </div>
      </EditPageLayout>
    );
  }

  const isProfileRoute = location.pathname === '/profile';
  
  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={displayName}
        description={t('pages.users.details.description')}
        onBack={isProfileRoute ? undefined : () => navigate(-1)}
        actions={
          <Button
            variant="outline"
            onClick={() => setIsEditMode(true)}
            disabled={isSaving}
          >
            <Edit className="mr-2 h-4 w-4" />
            {t('pages.users.details.actions.edit')}
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <CardContent className="space-y-10 px-8 py-10">
          <div className="grid gap-6 md:grid-cols-2">
              {/* Personal Information */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="mb-4 text-lg font-semibold">
                    {t('pages.users.details.sections.personalInfo')}
                  </h2>
                  <div className="space-y-4">
                    {user.profileImage && (
                      <div className="mb-4">
                        <img
                          src={user.profileImage}
                          alt={displayName}
                          className="h-24 w-24 rounded-full object-cover"
                        />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {t('pages.users.details.fields.name')}
                      </p>
                      <p className="mt-1 text-sm">{displayName}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {t('pages.users.details.fields.email')}
                      </p>
                      <p className="mt-1 text-sm">{user.email}</p>
                    </div>
                    {user.firstName && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          {t('pages.users.details.fields.firstName')}
                        </p>
                        <p className="mt-1 text-sm">{user.firstName}</p>
                      </div>
                    )}
                    {user.lastName && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          {t('pages.users.details.fields.lastName')}
                        </p>
                        <p className="mt-1 text-sm">{user.lastName}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Account Information */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="mb-4 text-lg font-semibold">
                    {t('pages.users.details.sections.accountInfo')}
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {t('pages.users.details.fields.role')}
                      </p>
                      <p className="mt-1 text-sm uppercase tracking-wide">
                        {t(`pages.users.invites.roles.${user.role}`, {
                          defaultValue: user.role,
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {t('pages.users.details.fields.status')}
                      </p>
                      <div className="mt-1 flex items-center gap-2">
                        {pending ? (
                          <StatusBadge variant="warning">
                            {t('pages.users.invites.status.pending')}
                          </StatusBadge>
                        ) : (
                          <StatusBadge variant="success">
                            {t('pages.users.invites.status.accepted')}
                          </StatusBadge>
                        )}
                        {!isActive && (
                          <StatusBadge variant="danger">
                            {t('pages.users.details.status.disabled')}
                          </StatusBadge>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {t('pages.users.details.fields.createdAt')}
                      </p>
                      <p className="mt-1 text-sm">{formatDate(user.createdAt)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Invitation Information */}
              {(pending || user.inviter) && (
                <Card className="md:col-span-2">
                  <CardContent className="p-6">
                    <h2 className="mb-4 text-lg font-semibold">
                      {t('pages.users.details.sections.invitationInfo')}
                    </h2>
                    <div className="grid gap-4 md:grid-cols-2">
                      {user.inviter && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">
                            {t('pages.users.details.fields.invitedBy')}
                          </p>
                          <p className="mt-1 text-sm">{inviterName}</p>
                        </div>
                      )}
                      {pending && user.invitationExpiresAt && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">
                            {t('pages.users.details.fields.invitationExpiresAt')}
                          </p>
                          <p className="mt-1 text-sm">
                            {formatDate(user.invitationExpiresAt)}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
        </CardContent>
        {!isEditMode && (
          <CardFooter className="border-t px-8 py-6">
            <div className="flex w-full flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {pending ? (
                  // Actions for pending users (not yet accepted invitation)
                  !isCurrentUser() && (
                    <Button
                      variant="destructive"
                      onClick={() => setIsCancelInvitationDialogOpen(true)}
                      disabled={isCancelingInvitation}
                    >
                      <X className="mr-2 h-4 w-4" />
                      {t('pages.users.actions.cancelInvitation')}
                    </Button>
                  )
                ) : (
                  // Actions for accepted users
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setIsResetPasswordDialogOpen(true)}
                      disabled={isResettingPassword}
                    >
                      <Key className="mr-2 h-4 w-4" />
                      {t('pages.users.details.actions.resetPassword')}
                    </Button>
                    {!isCurrentUser() && (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => setIsToggleActiveDialogOpen(true)}
                          disabled={isSaving}
                        >
                          {isActive ? (
                            <>
                              <Ban className="mr-2 h-4 w-4" />
                              {t('pages.users.details.actions.disable')}
                            </>
                          ) : (
                            <>
                              <CheckCircle className="mr-2 h-4 w-4" />
                              {t('pages.users.details.actions.enable')}
                            </>
                          )}
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => setIsDeleteDialogOpen(true)}
                          disabled={isDeleting}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t('pages.users.details.actions.delete')}
                        </Button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardFooter>
        )}
      </Card>

      {/* Reset Password Dialog */}
      <Dialog
        open={isResetPasswordDialogOpen}
        onOpenChange={setIsResetPasswordDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('pages.users.details.dialogs.resetPassword.title')}
            </DialogTitle>
            <DialogDescription>
              {t('pages.users.details.dialogs.resetPassword.description', {
                email: user.email,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsResetPasswordDialogOpen(false)}
              disabled={isResettingPassword}
            >
              {t('pages.users.details.actions.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleResetPassword}
              disabled={isResettingPassword}
            >
              {isResettingPassword
                ? t('pages.users.details.actions.resetting')
                : t('pages.users.details.actions.resetPassword')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Invitation Dialog */}
      <ConfirmationDialog
        open={isCancelInvitationDialogOpen}
        onOpenChange={setIsCancelInvitationDialogOpen}
        title={t('pages.users.dialogs.cancelInvitation.title')}
        description={t('pages.users.dialogs.cancelInvitation.description', {
          email: user?.email ?? '',
        })}
        confirmText={t('pages.users.actions.cancelInvitation')}
        onConfirm={handleCancelInvitation}
        isLoading={isCancelingInvitation}
        variant="destructive"
      />

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('pages.users.details.dialogs.delete.title')}
            </DialogTitle>
            <DialogDescription>
              {t('pages.users.details.dialogs.delete.description', {
                email: user.email,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              {t('pages.users.details.actions.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting
                ? t('pages.users.details.actions.deleting')
                : t('pages.users.details.actions.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toggle Active Dialog */}
      <ConfirmationDialog
        open={isToggleActiveDialogOpen}
        onOpenChange={setIsToggleActiveDialogOpen}
        title={t(
          isActive
            ? 'pages.users.details.dialogs.toggleActive.disable.title'
            : 'pages.users.details.dialogs.toggleActive.enable.title',
        )}
        description={t(
          isActive
            ? 'pages.users.details.dialogs.toggleActive.disable.description'
            : 'pages.users.details.dialogs.toggleActive.enable.description',
          { email: user.email },
        )}
        confirmText={
          isTogglingActive
            ? t('pages.users.details.actions.toggling')
            : isActive
              ? t('pages.users.details.actions.disable')
              : t('pages.users.details.actions.enable')
        }
        cancelText={t('pages.users.details.actions.cancel')}
        variant={isActive ? 'destructive' : 'default'}
        isLoading={isTogglingActive}
        onConfirm={handleToggleActive}
      />
    </div>
  );
}
